<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input } from '@sveltestrap/sveltestrap';

  import { getNotificationsContext } from '@client/components/notifications';

  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { generateUniqueRulename } from '@client/utils/rulenames';
  import { sanitizeInputValue } from '@client/utils/sanitize';

  import { deepCopy } from '@common/utils/objects';

  import { GeneralCloneForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let campaigns = [];
  let campaignForValidation = { rulename: '' };
  let invalidRulename = false;

  onMount(async () => {
    const allCampaigns = await CampaignsService.getAll();
    campaigns = allCampaigns.map(({ rulename }) => rulename).filter(Boolean);
  });

  const getCampaigns = async () => {
    const campaigns = await CampaignsService.getAll();
    return campaigns.map((campaign) => ({
      label: campaign.rulename,
      value: campaign.rulename,
      ...campaign
    }));
  };

  const validateRulename = () => {
    if (campaignForValidation.rulename !== '') {
      campaignForValidation.rulename = sanitizeInputValue(campaignForValidation.rulename);
    }
  };

  $: invalidRulename = campaignForValidation.rulename !== '' && campaigns.includes(campaignForValidation.rulename);

  const onCloneCampaign = async (campaign, newRulename) => {
    const rulename = campaignForValidation.rulename || generateUniqueRulename(campaign.rulename);

    const cloneData = {
      ...deepCopy(campaign),
      rulename,
      _id: null
    };

    const activeTargeting = await TargetingService.get();

    const currentAccountTargeting = activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
      .map(({ name }) => name);

    currentAccountTargeting.forEach((name) => {
      const existingAccount = activeTargeting.accounts.find((account) => account.name === name);

      if (existingAccount) {
        const existingAd = existingAccount.ads.find(({ text }) => text === cloneData.rulename);

        if (!existingAd) {
          existingAccount.ads.push({
            text: cloneData.rulename
          });
        }
      } else {
        activeTargeting.accounts.push({
          name,
          ads: [
            {
              text: cloneData.rulename
            }
          ],
          tags: [],
          isNew: true
        });
      }
    });

    const cloneTargetingResponse = await TargetingService.update(activeTargeting);

    if (cloneTargetingResponse?.status >= 400) {
      notify($format('label.FAILURE.CAMPAIGN_CLONE', { values: { campaign: campaign.rulename } }), 'error');
      throw new Error('Failed to update targeting for cloned campaign');
    }

    const cloneCampaignResponse = await CampaignsService.createCampaign(cloneData);

    if (cloneCampaignResponse?.status >= 400) {
      notify($format('label.FAILURE.CAMPAIGN_CLONE', { values: { campaign: campaign.rulename } }), 'error');
      throw new Error('Failed to clone campaign');
    }

    notify($format('label.SUCCESS.CAMPAIGN_CLONE', { values: { campaign: campaign.rulename } }));

    step.data = {
      id: rulename,
      originalId: campaign.rulename
    };
  };

  const rollbackCloneCampaign = async () => {
    if (!step.data?.id) {
      return; // Nothing to rollback
    }

    try {
      // Get the cloned campaign
      const campaigns = await CampaignsService.getAll();
      const campaign = campaigns.find((c) => c.rulename === step.data.id);

      if (campaign) {
        const activeTargeting = await TargetingService.get();

        const currentAccountTargeting = activeTargeting.accounts
          .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
          .map(({ name }) => name);

        const updatedAccounts = activeTargeting.accounts.map((account) => {
          if (currentAccountTargeting.includes(account.name)) {
            account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
          }

          return account;
        });

        const combinedTargeting = {
          accounts: updatedAccounts,
          departments: activeTargeting.departments
        };

        await TargetingService.update(combinedTargeting);
        await CampaignsService.deleteCampaign(campaign);
      }
    } catch (error) {
      console.error('Failed to rollback campaign clone:', error);
    }
  };
</script>

<GeneralCloneForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackCloneCampaign}
  getFunction={getCampaigns}
  cloneFunction={onCloneCampaign}
  bind:inputInvalid={invalidRulename}
>
  <Input
    slot="input"
    type="text"
    placeholder={$format('label.PLACEHOLDER.INPUT', {
      values: { param: $format('label.NAME').toLowerCase() }
    })}
    bind:invalid={invalidRulename}
    feedback={$format('label.VALIDATION_NAME_EXISTS', {
      values: { type: $format('label.CAMPAIGN'), param: $format('label.NAME').toLowerCase() }
    })}
    on:keyup={validateRulename}
    bind:value={campaignForValidation.rulename}
  />
</GeneralCloneForm>
